import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { applyTheme, abs } from "../lib/theme";
import { compressImage } from "../lib/imageOptimizer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  GraduationCap, Search, Loader2, CheckCircle2, FileText, Award,
  PhoneCall, Download, ArrowLeft, ShieldCheck, AlertTriangle, MinusCircle, UploadCloud, X,
  Mail, Clock, Check, Printer, Receipt, CalendarDays, IndianRupee,
} from "lucide-react";

const REUPLOAD_ACCEPT = "image/jpeg,image/png,application/pdf";
const REUPLOAD_MAX_MB = 10;

// Same loader as Apply.jsx's own payment step — duplicated locally rather
// than shared, matching how self-contained every page in this codebase
// already is; window.Razorpay is a safe global to check for either caller.
const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const el = document.querySelector(`script[src="${RZP_SRC}"]`);
    if (el) { el.addEventListener("load", () => resolve(true)); el.addEventListener("error", () => resolve(false)); return; }
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STAGE_LABELS = [
  { id: "lead", label: "Lead Captured" },
  { id: "application_initiated", label: "Application Initiated" },
  { id: "doc_verified", label: "Document Verified" },
  { id: "offer_issued", label: "Offer Issued" },
  { id: "fee_paid", label: "Fee Paid" },
  { id: "enrolled", label: "Enrolled" },
];
const STAGE_INDEX = Object.fromEntries(STAGE_LABELS.map((s, i) => [s.id, i]));

// The 5-milestone journey chevron, mapped from the real 6-value
// applications.stage pipeline (STAGE_LABELS above) plus the two signals
// stage alone can't express: a rejected document (which doesn't roll the
// stage backward) and an actual successful payment (fee_status can say
// "due" even after doc_verified rolls back from a refund — see the
// backend's own refund-rollback notes). Step 1 "Application Started" is
// always completed: reaching this page at all means a trackable
// application record already exists, regardless of what the CRM's
// internal pipeline stage currently says.
function computeJourneySteps(data) {
  const stageIdx = STAGE_INDEX[data.current_stage] ?? 0;
  const anyRejected = data.documents?.some((d) => d.status === "rejected");
  const offerIssued = !!data.offer?.issued;
  const feePaid = !!data.payment?.paid || data.offer?.fee_status === "paid";
  const enrolled = data.current_stage === "enrolled";

  const docsState = anyRejected ? "blocked" : stageIdx >= STAGE_INDEX.doc_verified ? "completed" : "current";
  const offerState = offerIssued ? "completed" : docsState === "completed" ? "current" : "pending";
  const feeState = feePaid ? "completed" : offerIssued ? "current" : "pending";
  const enrollState = enrolled ? "completed" : feePaid ? "current" : "pending";

  return [
    { id: "started", label: "Application Started", state: "completed" },
    { id: "docs", label: "Document Verification", state: docsState },
    { id: "offer", label: "Offer Letter Issued", state: offerState },
    { id: "fee", label: "Seat Fee Confirmation", state: feeState },
    { id: "enroll", label: "Enrollment Complete", state: enrollState },
  ];
}

const STEP_TONE = {
  completed: { fill: "bg-emerald-600", text: "text-white", pillCls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  blocked: { fill: "bg-rose-500", text: "text-white", pillCls: "bg-rose-50 text-rose-700 border-rose-200" },
  pending: { fill: "bg-slate-100", text: "text-slate-400", pillCls: "bg-slate-50 text-slate-400 border-slate-200" },
  // "current" uses the institution's own brand color, applied inline below.
};

/** Desktop: an interlocking chevron row (clip-path arrows). Mobile: a
 * vertical pill list — chevron overlap math doesn't degrade gracefully at
 * narrow widths, so it gets its own simpler layout rather than a squashed
 * version of the desktop one. */
function ChevronStepper({ steps, primary }) {
  // Fixed-pixel notch depth (not a %) so the arrow tip and the segment
  // overlap that interlocks with it stay in sync at ANY container width —
  // a percentage-based notch only lines up with a fixed overlap margin at
  // the one container width it was tuned for; a px-based one holds from a
  // cramped 640px tablet up through a 1440px laptop without gaps or
  // overlap-driven text clipping.
  const NOTCH = 18;
  return (
    <div data-testid="journey-stepper">
      {/* Desktop / tablet chevron row */}
      <div className="hidden sm:flex w-full" data-testid="journey-stepper-desktop">
        {steps.map((s, i) => {
          const isCurrent = s.state === "current";
          const tone = STEP_TONE[s.state];
          const first = i === 0;
          const last = i === steps.length - 1;
          const clipPath = first
            ? `polygon(0% 0%, calc(100% - ${NOTCH}px) 0%, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0% 100%)`
            : last
            ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, ${NOTCH}px 50%)`
            : `polygon(0% 0%, calc(100% - ${NOTCH}px) 0%, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0% 100%, ${NOTCH}px 50%)`;
          return (
            <div
              key={s.id}
              className={`relative flex-1 min-w-0 flex items-center justify-center min-h-16 text-center px-4 py-2 ${!first ? "-ml-[18px]" : ""} ${isCurrent ? "text-white" : `${tone.text} ${tone.fill}`}`}
              style={{ clipPath, backgroundColor: isCurrent ? primary : undefined, zIndex: steps.length - i }}
              data-testid={`journey-step-${s.id}`}
              data-state={s.state}
            >
              {isCurrent && <span className="absolute inset-0 bg-white animate-pulse opacity-20" />}
              <span className="relative flex items-center gap-1.5 text-xs md:text-sm font-medium leading-snug">
                {s.state === "completed" ? (
                  <Check className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
                ) : s.state === "blocked" ? (
                  <AlertTriangle className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
                ) : null}
                <span className="min-w-0">{s.label}</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Mobile vertical pill fallback */}
      <div className="sm:hidden space-y-1.5" data-testid="journey-stepper-mobile">
        {steps.map((s) => {
          const isCurrent = s.state === "current";
          const tone = STEP_TONE[s.state];
          return (
            <div
              key={s.id}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm font-medium ${
                isCurrent ? "" : tone.pillCls
              }`}
              style={isCurrent ? { borderColor: primary, color: primary, backgroundColor: `${primary}14` } : undefined}
              data-testid={`journey-step-mobile-${s.id}`}
              data-state={s.state}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                  isCurrent ? "" : s.state === "completed" ? "bg-emerald-600 text-white" : s.state === "blocked" ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-500"
                }`}
                style={isCurrent ? { backgroundColor: primary } : undefined}
              >
                {s.state === "completed" ? <Check className="w-3 h-3" /> : s.state === "blocked" ? <AlertTriangle className="w-3 h-3" /> : (
                  <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? "bg-white" : "bg-slate-400"}`} />
                )}
              </span>
              {s.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DOC_STATUS = {
  approved: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: ShieldCheck },
  rejected: { label: "Needs re-upload", cls: "text-red-700 bg-red-50 border-red-200", icon: AlertTriangle },
  pending: { label: "Under review", cls: "text-amber-700 bg-amber-50 border-amber-200", icon: MinusCircle },
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function ApplicationTracker() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!!token);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ application_no: "", phone: "" });
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupErr, setLookupErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [paying, setPaying] = useState(null); // milestone currently in flight, or null

  const load = useCallback((tok) => {
    setLoading(true);
    api.get(`/public/applications/status/${tok}`)
      .then(({ data: d }) => {
        setData(d);
        if (d?.valid) {
          applyTheme({ branding: d.branding || {}, title: `Track application — ${d.institution_name || "EduPulse"}` });
        }
      })
      .catch(() => setData({ valid: false, detail: "Could not load this application right now." }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const submitLookup = async (e) => {
    e.preventDefault();
    if (!form.application_no.trim() || !form.phone.trim()) return;
    setLookupBusy(true);
    setLookupErr("");
    try {
      const { data: d } = await api.post("/public/applications/track", {
        application_no: form.application_no.trim(), phone: form.phone.trim(),
      });
      if (d?.valid) {
        navigate(`/track/${d.tracking_token}`, { replace: true });
      } else {
        setLookupErr(d?.detail || "No matching application found.");
      }
    } catch (e) {
      setLookupErr(formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLookupBusy(false);
    }
  };

  const acceptOffer = async () => {
    if (!token || accepting) return;
    setAccepting(true);
    try {
      await api.post(`/public/applications/${token}/offer/accept`);
      load(token);
    } catch (e) {
      setLookupErr(formatApiError(e.response?.data?.detail) || "Could not record your acceptance. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  const MILESTONE_LABEL = {
    application_fee: "Application fee", seat_deposit: "Seat deposit", tuition: "Tuition balance",
  };

  const payMilestone = async (milestone) => {
    if (!token || paying) return;
    setPaying(milestone);
    const label = MILESTONE_LABEL[milestone] || "Fee";
    try {
      const { data: res } = await api.post(`/track/${token}/pay`, { milestone });
      if (res.status === "paid") {
        toast.success(`${label} paid!`);
        load(token);
        return;
      }
      if (res.status === "requires_gateway" && res.provider === "razorpay") {
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Could not reach the payment gateway. Please retry."); return; }
        await new Promise((resolve) => {
          const rzp = new window.Razorpay({
            key: res.key_id,
            order_id: res.order_id,
            amount: res.amount,
            currency: res.currency,
            name: data?.institution_name || label,
            description: label,
            theme: { color: data?.branding?.primary_color || "#1E40AF" },
            handler: async (resp) => {
              try {
                await api.post(`/track/${token}/razorpay/verify`, {
                  milestone,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                  method: "Razorpay",
                });
                toast.success(`${label} paid!`);
                load(token);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
              } finally { resolve(); }
            },
            modal: { ondismiss: () => resolve() },
          });
          rzp.open();
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start the payment. Please try again.");
    } finally {
      setPaying(null);
    }
  };

  const primary = data?.branding?.primary_color || "#1E40AF";
  const logo = abs(data?.branding?.logo_url);
  // The status view's chevron stepper and stacked cards need real room to
  // breathe (that's this whole fix); the lookup form is two fields and a
  // button, and stretching it to the same width just leaves it looking
  // sparse — so only the loaded, valid-application state gets the wide
  // container, everything else keeps the original narrow one.
  const wide = !!data?.valid;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center px-4 py-8 sm:py-14">
      <div className={`w-full mx-auto transition-[max-width] ${wide ? "max-w-4xl" : "max-w-lg"}`}>
        <Header logo={logo} name={data?.institution_name} primary={primary} />

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/70 border border-slate-200 overflow-hidden">
          {token && loading ? (
            <div className="p-10 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-sm">Loading your application…</p>
            </div>
          ) : !token || (data && !data.valid) ? (
            <LookupForm
              form={form} setForm={setForm} onSubmit={submitLookup}
              busy={lookupBusy} err={lookupErr || (data && !data.valid ? data.detail : "")}
              primary={primary} showBackNotice={!!token}
            />
          ) : data?.valid ? (
            <StatusView
              data={data} primary={primary} accepting={accepting} onAccept={acceptOffer}
              paying={paying} onPayMilestone={payMilestone}
              onTrackAnother={() => { setData(null); navigate("/track"); }}
              onReuploaded={() => load(token)}
            />
          ) : null}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">Powered by EduPulse Admissions</p>
      </div>
    </div>
  );
}

function Header({ logo, name, primary }) {
  return (
    <div className="flex items-center gap-3 mb-5 px-1">
      {logo ? (
        <img src={logo} alt={name || "Institution"} className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-200 p-1"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: primary }}>
          <GraduationCap className="w-5 h-5" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-slate-800 truncate">{name || "Admissions Office"}</div>
        <div className="text-xs text-slate-500">Application status tracker</div>
      </div>
    </div>
  );
}

function LookupForm({ form, setForm, onSubmit, busy, err, primary, showBackNotice }) {
  return (
    <form onSubmit={onSubmit} className="p-5 sm:p-6 space-y-4" data-testid="tracker-lookup-form">
      {showBackNotice && (
        <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          That link didn't lead anywhere. Look up your application below instead.
        </p>
      )}
      <div>
        <h2 className="font-semibold text-slate-800">Track your application</h2>
        <p className="text-sm text-slate-500 mt-0.5">Enter your application number and registered mobile number.</p>
      </div>
      <div>
        <Label className="text-xs">Application Number</Label>
        <Input className="mt-1 font-mono" data-testid="tracker-app-no" placeholder="AURO/2026/0412"
          value={form.application_no} onChange={(e) => setForm((s) => ({ ...s, application_no: e.target.value }))} />
      </div>
      <div>
        <Label className="text-xs">Registered Mobile Number</Label>
        <Input className="mt-1" type="tel" data-testid="tracker-phone" placeholder="10-digit mobile number"
          value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} />
      </div>
      {err && (
        <p className="text-sm text-red-600 flex items-start gap-1.5">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{err}</span>
        </p>
      )}
      <Button type="submit" disabled={busy} className="w-full h-11 text-[15px] font-semibold text-white"
        style={{ backgroundColor: primary }} data-testid="tracker-lookup-submit">
        {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</>) : (<><Search className="w-4 h-4 mr-2" /> Track Application</>)}
      </Button>
    </form>
  );
}

function StatusView({ data, primary, accepting, onAccept, paying, onPayMilestone, onTrackAnother, onReuploaded }) {
  const journeySteps = computeJourneySteps(data);
  const anyRejected = data.documents?.some((d) => d.status === "rejected");

  const jumpToDocuments = () =>
    document.getElementById("tracker-documents-section")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="p-5 sm:p-6 space-y-6">
      {/* Print-only summary sheet is styled via the @media print block below;
          everything else on the page carries .print-hide. */}
      <style>{`
        @media print {
          .print-hide { display: none !important; }
          body { background: #fff; }
        }
      `}</style>

      {/* Header: program title, application no, key dates, prominent CTA */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs text-slate-400 font-mono" data-testid="tracker-app-no-display">
            Application No: {data.application_no}
          </div>
          <h2 className="font-semibold text-lg text-slate-800 mt-0.5">{data.course_name}</h2>
          <p className="text-sm text-slate-500">{data.applicant_name}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
            {data.created_at && (
              <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Submitted {fmtDate(data.created_at)}</span>
            )}
            {data.payment?.paid_at && (
              <span className="flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Fee paid {fmtDate(data.payment.paid_at)}</span>
            )}
          </div>
        </div>

        {anyRejected ? (
          <button type="button" onClick={jumpToDocuments} className="print-hide shrink-0" data-testid="tracker-cta-reupload">
            <Button className="h-9 bg-rose-600 hover:bg-rose-700 text-white">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Re-upload Pending Documents
            </Button>
          </button>
        ) : data.offer?.issued ? (
          <a href={abs(data.offer.offer_letter_url)} target="_blank" rel="noreferrer" className="print-hide shrink-0" data-testid="tracker-cta-offer">
            <Button className="h-9 text-white" style={{ backgroundColor: primary }}>
              <Download className="w-4 h-4 mr-1.5" /> Download Offer Letter
            </Button>
          </a>
        ) : null}
      </div>

      {/* Chevron process stepper */}
      <ChevronStepper steps={journeySteps} primary={primary} />

      {/* Quick archival download bar */}
      <div className="print-hide flex flex-wrap gap-2" data-testid="tracker-download-bar">
        <button type="button" onClick={() => window.print()}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="download-chip-summary">
          <Printer className="w-3.5 h-3.5" /> Application Summary (Print / Save as PDF)
        </button>
        {data.payment?.receipt_url && (
          <a href={abs(data.payment.receipt_url)} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="download-chip-receipt">
            <Receipt className="w-3.5 h-3.5" /> Fee Receipt
          </a>
        )}
        {data.offer?.issued && (
          <a href={abs(data.offer.offer_letter_url)} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="download-chip-offer">
            <Award className="w-3.5 h-3.5" /> Offer Letter
          </a>
        )}
      </div>

      {/* Timeline */}
      {data.timeline?.length > 0 && (
        <div className="space-y-2.5">
          {data.timeline.map((t, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
              <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                <span className="text-sm text-slate-700">
                  {STAGE_LABELS.find((s) => s.id === t.stage)?.label || t.stage}
                </span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(t.completed_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {data.documents?.length > 0 && (
        <div id="tracker-documents-section">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Documents</h3>
          <div className="space-y-1.5">
            {data.documents.map((d, i) => (
              <DocRow key={i} doc={d} token={data.tracking_token} primary={primary} onReuploaded={onReuploaded} />
            ))}
          </div>
        </div>
      )}

      {/* Application fee — the one milestone payable even with no offer yet */}
      {data.application_fee && (
        <div className="rounded-xl border-2 p-4" style={{ borderColor: data.application_fee.paid ? "#10b981" : primary }}
          data-testid="tracker-application-fee-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                <Receipt className="w-4 h-4" style={{ color: primary }} /> Application Fee
              </div>
              <div className="text-xs text-slate-500 mt-1">
                ₹{data.application_fee.amount.toLocaleString("en-IN")} (₹{data.application_fee.base.toLocaleString("en-IN")} + 18% GST)
              </div>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${data.application_fee.paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
              data-testid="tracker-application-fee-status">
              {data.application_fee.paid ? "Paid" : "Unpaid"}
            </span>
          </div>
          <div className="print-hide mt-3">
            {data.application_fee.paid ? (
              <a href={abs(`/api/track/${data.tracking_token}/fee-receipt?milestone=application_fee`)} target="_blank" rel="noreferrer">
                <Button variant="outline" className="w-full h-10">
                  <Receipt className="w-4 h-4 mr-1.5" /> Download Receipt
                </Button>
              </a>
            ) : (
              <Button onClick={() => onPayMilestone("application_fee")} disabled={!!paying}
                className="w-full h-10 text-white font-semibold" style={{ backgroundColor: primary }}
                data-testid="tracker-pay-application-fee">
                {paying === "application_fee" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Pay Application Fee (₹{data.application_fee.amount.toLocaleString("en-IN")})</>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Offer */}
      {data.offer?.issued && (
        <div className="rounded-xl border-2 p-4" style={{ borderColor: primary }} data-testid="tracker-offer-card">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Award className="w-4 h-4" style={{ color: primary }} /> Admission Offer
          </div>
          <div className="text-xs text-slate-400 font-mono mt-1">{data.offer.serial_no}</div>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-slate-500">Fee status</span>
            <span className={`font-medium ${data.offer.fee_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>
              {data.offer.fee_status === "paid" ? "Paid" : `₹${Number(data.offer.fee_due || 0).toLocaleString("en-IN")} due`}
            </span>
          </div>
          <div className="print-hide flex flex-col sm:flex-row gap-2 mt-4">
            <a href={abs(data.offer.offer_letter_url)} target="_blank" rel="noreferrer" className="flex-1">
              <Button variant="outline" className="w-full h-10">
                <Download className="w-4 h-4 mr-1.5" /> Download Offer Letter
              </Button>
            </a>
            {!data.offer.accepted ? (
              <Button onClick={onAccept} disabled={accepting} className="flex-1 h-10 text-white" style={{ backgroundColor: primary }}
                data-testid="tracker-accept-offer">
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept Offer"}
              </Button>
            ) : data.offer.seat_deposit_paid ? (
              <Button disabled className="flex-1 h-10 bg-emerald-600 text-white opacity-90">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Seat Deposit Paid
              </Button>
            ) : (
              <Button onClick={() => onPayMilestone("seat_deposit")} disabled={!!paying} className="flex-1 h-10 text-white font-semibold"
                style={{ backgroundColor: primary }} data-testid="tracker-pay-deposit">
                {paying === "seat_deposit" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Pay Seat Deposit (₹{Number(data.offer.seat_deposit_amount || 0).toLocaleString("en-IN")})</>
                )}
              </Button>
            )}
          </div>
          {data.offer.accepted && !data.offer.seat_deposit_paid && (
            <p className="text-xs text-slate-400 mt-2">
              Offer accepted on {fmtDate(data.offer.accepted_at)} — pay the seat deposit to confirm your seat.
            </p>
          )}
        </div>
      )}

      {/* Tuition balance — the remaining amount after the seat deposit */}
      {data.offer?.issued && data.offer.tuition_amount > 0 && !data.offer.tuition_paid && (
        <div className="rounded-xl border-2 border-slate-200 p-4" data-testid="tracker-tuition-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                <IndianRupee className="w-4 h-4" style={{ color: primary }} /> Tuition Balance
              </div>
              <div className="text-xs text-slate-500 mt-1">Remaining balance after your seat deposit</div>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">Unpaid</span>
          </div>
          <Button onClick={() => onPayMilestone("tuition")} disabled={!!paying}
            className="print-hide w-full h-10 mt-3 text-white font-semibold" style={{ backgroundColor: primary }}
            data-testid="tracker-pay-tuition">
            {paying === "tuition" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>Pay Tuition Balance (₹{Number(data.offer.tuition_amount).toLocaleString("en-IN")})</>
            )}
          </Button>
        </div>
      )}

      <div className="print-hide">
        <CounselorCard data={data} />
      </div>

      <button type="button" onClick={onTrackAnother}
        className="print-hide w-full text-center text-sm text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1.5 pt-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Track a different application
      </button>
    </div>
  );
}

// Lucide has no WhatsApp brand mark — a small inline glyph reads as
// unmistakably "WhatsApp" (the recognition that's the whole point of this
// CTA) in a way a generic chat-bubble icon wouldn't.
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12.001 2C6.478 2 2 6.477 2 12c0 1.87.514 3.622 1.408 5.121L2 22l5.03-1.378A9.953 9.953 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2Zm0 18.062a8.03 8.03 0 0 1-4.372-1.28l-.313-.19-3.038.833.822-2.981-.204-.316A8.02 8.02 0 0 1 3.938 12c0-4.45 3.612-8.062 8.062-8.062S20.062 7.55 20.062 12 16.451 20.062 12 20.062Z" />
    </svg>
  );
}

/** "Your Dedicated Admissions Officer" card — the assigned counselor, or the
 * institution's general helpdesk if nobody's assigned yet (see the backend's
 * _counselor_or_helpdesk); renders nothing at all when neither has a
 * reachable phone or email, per the "hide cleanly, don't break layout"
 * requirement. */
function CounselorCard({ data }) {
  const c = data.counselor;
  if (!c || (!c.whatsapp_number && !c.phone && !c.email)) return null;

  const initials = (c.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "AO";

  const anyRejected = data.documents?.some((d) => d.status === "rejected");
  const context = anyRejected
    ? "my document re-upload verification"
    : data.offer?.issued
    ? "my offer letter and seat reservation fee"
    : "my application status";

  const message =
    `Hello ${c.name || "Admissions Team"}, my name is ${data.applicant_name} ` +
    `(App No: ${data.application_no}) for ${data.course_name}. I am reviewing my ` +
    `application on the portal and had a query regarding ${context}.`;
  const waLink = c.whatsapp_number
    ? `https://wa.me/${c.whatsapp_number}?text=${encodeURIComponent(message)}`
    : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="counselor-card">
      <div className="flex items-center gap-3">
        {c.avatar_url ? (
          <img src={abs(c.avatar_url)} alt={c.name}
            className="h-12 w-12 rounded-full object-cover border border-slate-200 shrink-0" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-slate-800 text-white flex items-center justify-center font-semibold text-sm shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">
            Your Dedicated Admissions Officer
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
          {c.role && <div className="text-xs text-slate-500 truncate">{c.role}</div>}
        </div>
      </div>

      {c.office_hours && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-3">
          <Clock className="w-3.5 h-3.5 shrink-0" /> {c.office_hours}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mt-3.5">
        {waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="counselor-whatsapp-cta">
            <Button className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
              <WhatsAppIcon className="w-4 h-4 mr-2" /> Chat on WhatsApp
            </Button>
          </a>
        )}
        {(c.phone || c.email) && (
          <div className={`flex gap-2 ${waLink ? "" : "flex-1"}`}>
            {c.phone && (
              <a href={`tel:${c.phone}`} className="flex-1" data-testid="counselor-call-cta">
                <Button variant="outline" className="w-full h-10">
                  <PhoneCall className="w-4 h-4 mr-2" /> Call
                </Button>
              </a>
            )}
            {c.email && (
              <a href={`mailto:${c.email}`} className="flex-1" data-testid="counselor-email-cta">
                <Button variant="outline" className="w-full h-10">
                  <Mail className="w-4 h-4 mr-2" /> Email
                </Button>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** One document row. Non-rejected docs render the plain status pill exactly
 * as before; a rejected doc additionally gets an "Action Required" callout
 * with the counselor's reason and an inline re-upload control, so a
 * candidate can fix it without leaving the tracker page. */
function DocRow({ doc, token, primary, onReuploaded }) {
  const st = DOC_STATUS[doc.status] || DOC_STATUS.pending;
  const Icon = st.icon;
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!file || file.type === "application/pdf") { setPreview(""); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const takeFile = (f) => {
    setErr("");
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type)) {
      setErr("Please choose a PDF, JPG or PNG file."); return;
    }
    if (f.size > REUPLOAD_MAX_MB * 1024 * 1024) {
      setErr(`That file is larger than ${REUPLOAD_MAX_MB} MB. Try a photo instead of a scan.`); return;
    }
    setFile(f);
  };

  const clear = () => { setFile(null); setErr(""); setProgress(0); };

  const submit = async () => {
    if (!file || busy || !token) return;
    setBusy(true);
    setErr("");
    try {
      let toSend = file;
      if (file.type.startsWith("image/")) {
        try { toSend = await compressImage(file, { maxSizeMB: Math.min(4, REUPLOAD_MAX_MB) }); } catch { toSend = file; }
      }
      const fd = new FormData();
      fd.append("file", toSend, toSend.name || file.name || "document");
      await api.post(`/track/${token}/documents/${doc.doc_type}/reupload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Document submitted successfully. Admissions team will verify shortly.");
      clear();
      onReuploaded?.();
    } catch (e) {
      const status = e.response?.status;
      setErr(
        status === 409
          ? "This document was just reviewed — refresh the page to see its latest status."
          : formatApiError(e.response?.data?.detail) || "Upload failed. Please check your connection and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${st.cls}`} data-testid="tracker-doc-row">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0">
          <FileText className="w-3.5 h-3.5 shrink-0 opacity-60" />
          <span className="truncate">{doc.label}</span>
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium">
          <Icon className="w-3.5 h-3.5" /> {st.label}
        </span>
      </div>

      {doc.status === "rejected" && (
        <div className="mt-2.5 -mx-1 rounded-lg bg-white/70 border border-red-200 p-3" data-testid="tracker-reupload-box">
          <div className="flex items-center gap-1.5 text-red-800 font-semibold text-xs uppercase tracking-wide">
            <AlertTriangle className="w-3.5 h-3.5" /> Action Required
          </div>
          {doc.rejection_reason && (
            <p className="text-sm text-slate-700 mt-1.5">{doc.rejection_reason}</p>
          )}

          {!file ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-2.5 w-full rounded-lg border-2 border-dashed border-red-300 bg-white hover:border-red-400 px-3 py-3 flex items-center justify-center gap-2 text-red-700 text-sm font-medium transition-colors"
              data-testid="tracker-reupload-trigger"
            >
              <UploadCloud className="w-4 h-4" /> Re-upload {doc.label}
            </button>
          ) : (
            <div className="mt-2.5 rounded-lg border border-slate-200 bg-white p-2.5 flex items-center gap-2.5">
              {preview ? (
                <img src={preview} alt="Preview" className="h-11 w-11 rounded-md object-cover border border-slate-200 shrink-0" />
              ) : (
                <div className="h-11 w-11 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{file.name}</div>
                <div className="text-xs text-slate-400">{fmtSize(file.size)}</div>
                {busy && (
                  <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: primary }} />
                  </div>
                )}
              </div>
              {!busy && (
                <button type="button" onClick={clear} aria-label="Remove file"
                  className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          <input
            ref={inputRef} type="file" accept={REUPLOAD_ACCEPT} capture="environment"
            className="hidden" data-testid="tracker-reupload-input"
            onChange={(e) => takeFile(e.target.files?.[0])}
          />

          {err && (
            <p className="text-xs text-red-600 flex items-start gap-1.5 mt-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
            </p>
          )}

          {file && (
            <Button
              onClick={submit} disabled={busy}
              className="mt-2.5 w-full h-9 text-sm font-semibold text-white"
              style={{ backgroundColor: primary }}
              data-testid="tracker-reupload-submit"
            >
              {busy ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Uploading… {progress}%</>) : "Submit Document"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
